/*
 * Copyright (c) 2018 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */
import {createSelector} from 'reselect';

import applications from '../applications';


const namespace = 'domainapp/';

/**
 * Action fired when the user requests navigation to a page in a domain application.
 */
export const NAVIGATE_TO_APPLICATION_REQUEST = `${namespace}NAVIGATE_TO_APPLICATION_REQUEST`;

/**
 * Action fired when the user's navigation request to a domain application page is complete.
 */
export const NAVIGATE_TO_APPLICATION_SUCCESS = `${namespace}NAVIGATE_TO_APPLICATION_SUCCESS`;

const INITIAL_STATE = {
  applicationId: null,
  pageId: null,
  isFetching: false
};

/**
 * Selector used to retrieve the current application's identifier from the redux state.
 *
 * @param state the current application state.
 * @returns the identifier of the active application, or {null} if no application is active.
 */
const activeApplicationSelector = state => state.ui && state.ui.domainapp && state.ui.domainapp.applicationId;

/**
 * Selector used to retrieve the current page's identifier from the redux state.
 *
 * @param state the current application state.
 * @returns the identifier of the active domain application page, or {null} if no domain application page is active.
 */
const activePageSelector = state => state.ui && state.ui.domainapp && state.ui.domainapp.pageId;

/**
 * Retrieve the active application from the Redux state.
 *
 * @state the current application state.
 * @returns an object identifying the active application and page.
 */
export const getActiveApplication = createSelector(
  [activeApplicationSelector, activePageSelector, applications.selectors.getVisibleApplications],
      (application, page, applications) => {

    // When a user uses a deep link to a domain application page, we need to calculate the active application. Seeing
    // as domain applications only tell us which page is active, we have to search the applications for a give page
    // to determine the active application.
    let appIdentifier = application;
    if (!appIdentifier) {
      applications.forEach(app => {
        app.links.forEach(link => {
          if (page === link.id) {
            appIdentifier = app.id;
          }
        });
      });
    }
    return {application: appIdentifier, page};
  }
);

/**
 * Reducer used to manage the `ui/domainapp` strand of the application state.
 *
 * @param state the current state
 * @param action the received action
 * @returns {*} the new state.
 */
export const reducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case NAVIGATE_TO_APPLICATION_REQUEST:
      const payload = action.payload;
      return {...state, ...{...payload, isFetching: true}};
    case NAVIGATE_TO_APPLICATION_SUCCESS:
      return {...state, ...{isFetching: false}};
    default:
      return state;
  }
};

/**
 * Action creator that navigates the user to a application URL.
 *
 * @param link an object containing an application identifier and a URL path within the application. An undefined
 * value will navigate away from the current application.
 *
 * @returns {function(*)}
 */
export const navigateToApplication = (link = {applicationId: null, pageId: null}) => {
  const payload = link;
  return dispatch => {
    dispatch({type: NAVIGATE_TO_APPLICATION_REQUEST, payload});
    // TODO: Fire this after the iframe loads.
    dispatch({type: NAVIGATE_TO_APPLICATION_SUCCESS, payload});
    return Promise.resolve();
  };
};



// WEBPACK FOOTER //
// ./src/DomainApplicationView/state.js